<template>
  <div class="blog-detail-wrapper">

    <!-- Фильтр -->
    <b-card no-body class="mb-2">
      <b-card-header class="pb-50">
        <h5 class="mb-0">
          Фильтр
        </h5>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col
              cols="12"
              md="4"
              class="mb-md-0 mb-2"
          >
            <label>Тип списков</label>
            <v-select
                v-model="type"
                :options="typeOptions"
                class="w-100"
                :reduce="val => val.value"
                :clearable="false"
            />
          </b-col>
          <b-col
              cols="12"
              md="5"
              class="mb-md-0 mb-2"
          >
            <label>Поиск по названию</label>
            <b-input-group>
              <b-form-input
                  v-model="search"
                  placeholder="Часть названия списка"
                  debounce="200"
              />
              <b-input-group-append>
                <b-button
                    variant="outline-secondary"
                    :disabled="!search"
                    @click="search = ''"
                >
                  <feather-icon icon="XIcon"/>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col
              cols="12"
              md="3"
              class="d-flex align-items-end"
          >
            <div class="mb-50">
              <b-form-checkbox
                  v-model="includeAuto"
                  class="mb-75"
              >
                Включая АВТО
              </b-form-checkbox>
              <b-form-checkbox v-model="onlyFree">
                Только свободные
              </b-form-checkbox>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Списки -->
    <b-card no-body>
      <b-card-header class="pb-50 d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          Списки
        </h5>
        <span class="text-muted">
          Показано {{ filteredLists.length }} из {{ listsList.length }}
        </span>
      </b-card-header>

      <b-overlay
          :show="listLoading"
          rounded="sm"
      >
        <b-table
            :items="filteredLists"
            responsive
            :fields="tableColumns"
            primary-key="id"
            show-empty
            empty-text="Списки не найдены"
            class="position-relative mb-0"
        >

          <!-- Column: Title -->
          <template #cell(title)="data">
            <div class="d-flex align-items-center">
              <b-form-input
                  v-model="data.item.title"
                  class="flex-grow-1"
                  style="min-width: 180px"
              />
              <b-button
                  v-if="isTitleChanged(data.item)"
                  v-b-tooltip.hover
                  title="Сохранить название"
                  variant="success"
                  class="btn-icon ml-50"
                  size="sm"
                  @click="saveTitle(data.item)"
              >
                <feather-icon icon="CheckIcon"/>
              </b-button>
              <b-link
                  v-if="!data.item.auto"
                  v-b-tooltip.hover
                  title="Открыть список"
                  class="ml-50"
                  :to="{name: 'list-view', params: { id: data.item.id }}"
              >
                <feather-icon icon="ExternalLinkIcon"/>
              </b-link>
            </div>
          </template>

          <!-- Column: Subtype -->
          <template #cell(subtype)="data">
            <div class="d-flex align-items-center">
              <v-select
                  v-model="data.item.subtype"
                  :options="subtypeOptions"
                  :reduce="val => val.value"
                  class="flex-grow-1"
                  style="min-width: 200px"
                  :clearable="true"
                  :append-to-body="true"
                  placeholder="Выберите подтип"
              />
              <b-button
                  v-if="isSubtypeChanged(data.item)"
                  v-b-tooltip.hover
                  title="Сохранить подтип"
                  variant="success"
                  class="btn-icon ml-50"
                  size="sm"
                  @click="saveSubtype(data.item)"
              >
                <feather-icon icon="CheckIcon"/>
              </b-button>
            </div>
          </template>

          <!-- Column: Autotopics -->
          <template #cell(autotopics)="data">
            <div
                v-if="autotopicsOf(data.item).length"
                class="d-flex flex-wrap"
            >
              <b-badge
                  v-for="autotopic in autotopicsOf(data.item)"
                  :key="autotopic.id"
                  variant="light-info"
                  class="mr-50 mb-25"
              >
                {{ autotopic.title }}
              </b-badge>
            </div>
            <b-badge
                v-else
                variant="light-secondary"
            >
              свободный
            </b-badge>
          </template>

          <!-- Column: Items count -->
          <template #cell(items_count)="data">
            <b-badge variant="light-primary">
              {{ data.item.items_count }}
            </b-badge>
          </template>

          <!-- Column: Auto -->
          <template #cell(auto)="data">
            <b-badge
                v-if="data.item.auto"
                variant="light-warning"
            >
              АВТО
            </b-badge>
            <span
                v-else
                class="text-muted"
            >—</span>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <div class="text-nowrap">
              <b-button
                  v-b-tooltip.hover
                  title="Удалить список"
                  variant="flat-danger"
                  class="btn-icon"
                  size="sm"
                  @click="confirmDelete(data.item)"
              >
                <feather-icon icon="TrashIcon"/>
              </b-button>
            </div>
          </template>
        </b-table>
      </b-overlay>
    </b-card>

    <!-- Новый список -->
    <b-card no-body class="mt-2">
      <b-card-header class="pb-50">
        <h5 class="mb-0">
          Новый список
        </h5>
      </b-card-header>
      <b-card-body>
        <b-overlay
            :show="createItemLoading"
            rounded="sm"
        >
          <b-input-group>
            <b-form-input
                v-model="newListName"
                placeholder="Название списка"
                @keyup.enter="createItem"
            />
            <b-input-group-append>
              <b-button
                  variant="success"
                  :disabled="!newListName.trim()"
                  @click.prevent="createItem"
              >
                <feather-icon icon="PlusIcon" class="mr-50"/>
                Добавить
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-overlay>
      </b-card-body>
    </b-card>

    <!-- Modal Delete Confirmation -->
    <b-modal
        id="modal-delete"
        ref="deleteModal"
        title="Подтверждение удаления"
        ok-variant="danger"
        ok-title="Удалить"
        cancel-title="Отмена"
        centered
        @ok="deleteItem"
    >
      <p>Вы уверены, что хотите удалить список "{{ itemToDelete ? itemToDelete.title : '' }}"?</p>
    </b-modal>

  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BLink,
  BBadge,
  BTable,
  BOverlay,
  BButton,
  BModal,
  BFormCheckbox,
  VBTooltip,
} from 'bootstrap-vue'
import store from "@/store";
import {computed, onUnmounted, ref, watch} from "@vue/composition-api";
import topicsStoreModule from "@/views/topics/topicsStoreModule";
import vSelect from "vue-select";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BLink,
    BBadge,
    BTable,
    BButton,
    BOverlay,
    BModal,
    BFormCheckbox,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup() {
    const STORE_MODULE_NAME = 'topics'

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, topicsStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    // Table Handlers
    const tableColumns = [
      {key: 'id', label: '#', sortable: true, thStyle: {width: '70px'}},
      {key: 'title', label: 'Название', sortable: true, thStyle: {minWidth: '260px'}},
      {key: 'subtype', label: 'Подтип', sortable: true, thStyle: {width: '280px'}},
      {key: 'autotopics', label: 'Автотопики', sortable: false, thStyle: {minWidth: '220px'}},
      {key: 'items_count', label: 'Элементов', sortable: true, thStyle: {width: '120px'}},
      {key: 'auto', label: 'Авто', sortable: true, thStyle: {width: '90px'}},
      {key: 'actions', label: '', thStyle: {width: '70px'}},
    ]

    const type = ref('music')
    const includeAuto = ref(false)
    const onlyFree = ref(false)
    const search = ref('')
    const typeOptions = [
      { label: 'Музыка', value: 'music' },
      { label: 'Фильмы', value: 'movies' },
      { label: 'Сериалы', value: 'series' },
    ]

    const subtypeOptions = [
      { label: 'Матрица (буквы подряд)', value: 'matrix_letters' },
      { label: 'Первая буква', value: 'first_letter' },
      { label: 'Страна', value: 'country' },
      { label: 'Матрица (инициалы)', value: 'matrix_initials' },
      { label: 'Матрица (начало/конец слова)', value: 'matrix_word_edges' },
      { label: 'Тематика', value: 'theme' },
      { label: 'Другие темы в названиях', value: 'other_naming_topics' },
      { label: 'Количество слов', value: 'word_count' },
      { label: 'Количество букв', value: 'letter_count' },
      { label: 'Жанры', value: 'genres' },
      { label: 'Символы в названии', value: 'symbols_in_title' },
      { label: 'Предлоги в названии', value: 'prepositions_in_title' },
      { label: 'Год выхода', value: 'release_year' },
      { label: 'Актеры', value: 'actors' },
      { label: 'Режиссеры', value: 'directors' },
      { label: 'Награды', value: 'awards' },
      { label: 'Студия', value: 'studio' },
      { label: 'Год начала деятельности', value: 'foundation_year' },
      { label: 'Год распада', value: 'dissolution_year' },
      { label: 'Год рождения', value: 'birth_year' },
      { label: 'Год смерти', value: 'death_year' },
    ]

    const originalSubtypes = ref({})
    const originalTitles = ref({})

    const listsList = ref([])
    const newListName = ref('')
    const createItemLoading = ref(false)
    const listLoading = ref(false)
    const lists = ref([])
    const freeLists = ref([])
    const itemToDelete = ref(null)
    const deleteModal = ref(null)
    const fetchLists = (...args) => store.dispatch('topics/fetchLists', ...args)
    const addList = (...args) => store.dispatch('topics/createList', ...args)
    const removeList = (...args) => store.dispatch('topics/deleteList', ...args)
    const updateListAction = (...args) => store.dispatch('topics/updateList', ...args)

    const autotopicsOf = (item) => item.autotopics || []

    const filteredLists = computed(() => {
      const query = search.value.trim().toLowerCase()

      return listsList.value.filter(item => {
        if (query && !String(item.title || '').toLowerCase().includes(query)) {
          return false
        }
        if (onlyFree.value && autotopicsOf(item).length) {
          return false
        }
        return true
      })
    })

    const fetchListsList = () => {
      listLoading.value = true;
      const params = {type: type.value}
      if (!includeAuto.value) {
        params.auto = 0
      }
      fetchLists({queryParams: params})
          .then(response => {
            listsList.value = response.data.data
            lists.value = response.data.lists
            freeLists.value = response.data.free_lists
            // Сохраняем оригинальные значения subtype и title
            const subtypes = {}
            const titles = {}
            response.data.data.forEach(item => {
              subtypes[item.id] = item.subtype
              titles[item.id] = item.title
            })
            originalSubtypes.value = subtypes
            originalTitles.value = titles
            listLoading.value = false;
          })
    }

    const createItem = () => {
      const title = newListName.value.trim()
      if (!title) return

      createItemLoading.value = true
      addList({queryParams: {title, type: type.value}}).then(response => {
        const created = response.data.data
        listsList.value.push(created)
        originalTitles.value = {...originalTitles.value, [created.id]: created.title}
        originalSubtypes.value = {...originalSubtypes.value, [created.id]: created.subtype}
        newListName.value = ''
        createItemLoading.value = false
      }).catch(() => {
        createItemLoading.value = false
      })
    }

    const confirmDelete = (item) => {
      itemToDelete.value = item
      deleteModal.value.show()
    }

    const deleteItem = () => {
      if (!itemToDelete.value) return
      listLoading.value = true
      removeList({id: itemToDelete.value.id}).then(() => {
        listsList.value = listsList.value.filter(item => item.id !== itemToDelete.value.id)
        itemToDelete.value = null
        listLoading.value = false
      }).catch(() => {
        listLoading.value = false
      })
    }

    const isSubtypeChanged = (item) => {
      return item.subtype !== originalSubtypes.value[item.id]
    }

    const saveSubtype = (item) => {
      updateListAction({id: item.id, queryParams: {subtype: item.subtype}}).then(() => {
        originalSubtypes.value = {...originalSubtypes.value, [item.id]: item.subtype}
      })
    }

    const isTitleChanged = (item) => {
      return item.title !== originalTitles.value[item.id]
    }

    const saveTitle = (item) => {
      updateListAction({id: item.id, queryParams: {title: item.title}}).then(() => {
        originalTitles.value = {...originalTitles.value, [item.id]: item.title}
      })
    }

    fetchListsList()

    watch([type, includeAuto], () => {
      fetchListsList()
    }, {
      deep: true,
    })

    return {
      fetchLists,
      fetchListsList,
      tableColumns,
      listsList,
      filteredLists,
      lists,
      autotopicsOf,
      createItem,
      createItemLoading,
      listLoading,
      newListName,

      typeOptions,
      type,
      includeAuto,
      onlyFree,
      search,

      itemToDelete,
      deleteModal,
      confirmDelete,
      deleteItem,

      subtypeOptions,
      isSubtypeChanged,
      saveSubtype,
      isTitleChanged,
      saveTitle,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
